import { useAtomValue } from 'jotai';
import isEmpty from 'lodash/isEmpty';
import React, { useMemo, useState } from 'react';
import { FormProvider, useForm, type ValidationRule } from 'react-hook-form';

import { BodyText, Button, FlexContainer, FlexItem } from '@placer-ui/components';
import { ControlledInputField } from 'components/controlled-input-field';
import type {
    RequestPropertyFormProps,
    SupportRequestResultsModalTableRow,
    SupportRequestResultsResponse,
} from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import { csvAddressFields } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/file-based-request-property/file-fields-mapping-config';
import { supportRequestAddressTypeStore } from 'features/my-zone/support-requests/store/property-store';
import { googleMapsUrlRules } from 'form-validations';
import {
    COORDINATES_ERROR_MESSAGES,
    LAT_REGEX,
    LONG_REGEX,
} from 'features/my-zone/support-requests/consts';
import {
    ChipList,
    type ChipListOptionType,
} from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/components/ask-for-review/components/chip-list/chip-list';
import { ChipListSkeleton } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/support-request-results-modal/components/ask-for-review/components/chip-list/chip-list-skeleton';
import { useGetResolverAlternativesQuery } from 'features/my-zone/support-requests/components/support-requests-modals/queries/get-resolver-alternatives-query';
import { getFeatureFlags } from 'core/flow-control';

export type NotMyPropertyFormValues = {
    name: string;
    formatted_address: string;
    lat: string;
    lng: string;
    zip_code: string;
    street: string;
    city: string;
    state: string;
    isDirty: boolean;
    challenge_googlemap_link: string;
    challenge_selected_alt: number;
    shouldRecheckCoverage: boolean;
    entity_name: string;
    entity_address: string;
};

type AlternativeOption = ChipListOptionType<number> & { option?: SupportRequestResultsResponse };

type LookingForDifferentPropertyProps = {
    onNextHandler: RequestPropertyFormProps['onNextHandler'];
    item: SupportRequestResultsModalTableRow;
};

const NoneOfTheAboveContent = ({ onNextHandler, item }: RequestPropertyFormProps) => {
    const supportRequestAddressType = useAtomValue(supportRequestAddressTypeStore);
    const [editMode, setEditMode] = useState(false);
    const [showLinkInput, setShowLinkInput] = useState(false);

    const defaultValues = {
        name: item?.name ?? '',
        formatted_address: item?.formatted_address ?? '',
        lat: item?.lat ?? '',
        lng: item?.lng ?? '',
        zip_code: item?.zip_code ?? '',
        street: item?.street ?? '',
        city: item?.city ?? '',
        state: item?.state ?? '',
        challenge_googlemap_link: '',
        challenge_selected_alt: -1,
    };

    const formController = useForm<NotMyPropertyFormValues>({
        defaultValues,
        mode: 'onBlur',
    });

    const {
        formState: { dirtyFields },
    } = formController;
    const { challenge_googlemap_link, ...relevantDirtyFields } = dirtyFields;
    const isFormDirty = !isEmpty(relevantDirtyFields);

    const handleSubmit = (data: NotMyPropertyFormValues) => {
        onNextHandler?.({ ...data, isDirty: isFormDirty, shouldRecheckCoverage: editMode });
    };

    const handleEditClick = () => {
        if (editMode) {
            formController.reset();
        }
        setEditMode((prev) => !prev);
    };

    return (
        <FormProvider {...formController}>
            <form
                onSubmit={(ev) => ev.preventDefault()}
                style={{ height: '100%', overflow: 'hidden' }}
            >
                <FlexContainer
                    flexDirection="column"
                    gap={8}
                    height="100%"
                    justifyContent="space-between"
                >
                    <div style={{ marginInlineEnd: 8 }}>
                        <BodyText element="p">
                            Confirm the details or click Edit to update.
                        </BodyText>
                        <BodyText element="p">Add a Google Maps link for reference.</BodyText>
                    </div>
                    <div style={{ overflow: 'auto' }}>
                        <FlexContainer flexDirection="column" gap={8} margin={'0 0 24px 0'}>
                            <ControlledInputField
                                name="name"
                                label="Name"
                                placeholder="Name"
                                maxLength={100}
                                required
                                asterix
                                testId="ask-review-name"
                                disabled={!editMode}
                            />

                            {csvAddressFields[supportRequestAddressType].map((item) => {
                                let validationPattern = {} as ValidationRule<RegExp>;
                                let inputType = 'text';

                                if (item.value === 'lat') {
                                    validationPattern = {
                                        value: LAT_REGEX,
                                        message: COORDINATES_ERROR_MESSAGES.lat,
                                    };
                                    inputType = 'number';
                                } else if (item.value === 'lng') {
                                    validationPattern = {
                                        value: LONG_REGEX,
                                        message: COORDINATES_ERROR_MESSAGES.lng,
                                    };
                                    inputType = 'number';
                                }

                                return (
                                    <ControlledInputField
                                        key={item.value}
                                        name={item.value}
                                        label={item.label}
                                        placeholder={item.label as string}
                                        maxLength={100}
                                        required
                                        asterix
                                        testId={`ask-review-${item.value}`}
                                        type={inputType}
                                        rules={
                                            validationPattern
                                                ? { pattern: validationPattern }
                                                : undefined
                                        }
                                        disabled={!editMode}
                                    />
                                );
                            })}

                            <ControlledInputField
                                name="zip_code"
                                label="Zip Code"
                                placeholder="Property Zip code"
                                maxLength={100}
                                testId="ask-review-zip-code"
                                disabled={!editMode}
                            />

                            {!editMode &&
                                (showLinkInput ? (
                                    <ControlledInputField
                                        name="challenge_googlemap_link"
                                        label="Link to Google Maps (optional)"
                                        rules={googleMapsUrlRules}
                                        placeholder="Type or paste the link"
                                        maxLength={100}
                                        testId="ask-review-gmap-link"
                                    />
                                ) : (
                                    <Button
                                        disabled={!formController.formState.isValid}
                                        variant="text"
                                        testId="ask-review-gmap-link-button"
                                        onClick={() => setShowLinkInput(true)}
                                    >
                                        Add Google Maps Link
                                    </Button>
                                ))}
                        </FlexContainer>
                    </div>
                    <FlexItem alignSelf="flex-end">
                        <FlexContainer gap={8} alignItems="center">
                            <Button
                                variant="secondary-gray"
                                size="small"
                                testId={`${editMode ? 'cancel' : 'edit'}-button`}
                                onClick={handleEditClick}
                                type="button"
                            >
                                {editMode ? 'Cancel' : 'Edit Details'}
                            </Button>
                            <Button
                                disabled={
                                    !formController.formState.isValid || (editMode && !isFormDirty)
                                }
                                variant="primary-gray"
                                size="small"
                                testId="submit-button"
                                onClick={formController.handleSubmit(handleSubmit)}
                            >
                                {editMode ? 'Recheck Coverage' : 'Confirm'}
                            </Button>
                        </FlexContainer>
                    </FlexItem>
                </FlexContainer>
            </form>
        </FormProvider>
    );
};

export const LookingForDifferentProperty = ({
    onNextHandler,
    item,
}: LookingForDifferentPropertyProps) => {
    const { id: row_id, alternative_token, alternative_count } = item;
    const { enable_submit_feedback_resolver_other_options_ff } = getFeatureFlags();
    const [noneOfAboveMode, setNoneOfAboveMode] = useState(
        !enable_submit_feedback_resolver_other_options_ff || alternative_count === 1,
    );

    const {
        data: resolverAlternatives,
        isLoading,
        isFetching,
    } = useGetResolverAlternativesQuery(row_id, alternative_token);

    const isQueryLoading = isLoading || isFetching;

    const handleOptionClick = ({ option, value }: AlternativeOption) => {
        if (value === -1) {
            setNoneOfAboveMode(true);
        } else {
            onNextHandler?.({
                ...option,
                challenge_selected_alt: value ? value : undefined,
                challenge_type: undefined,
            });
        }
    };

    const alternativesOptions = useMemo(() => {
        if (!resolverAlternatives) {
            return [];
        }

        const mappedData = resolverAlternatives
            .map((option, index) => ({
                label: option.entity_name || '',
                description: option.entity_address,
                value: index,
                testId: `alternative-option-${option.entity_name}`,
                option,
            }))
            .filter(({ option }) => option.entity_id !== item.entity_id);

        return [
            ...mappedData,
            {
                label: 'None of the above',
                value: -1,
                testId: 'none-of-the-above',
            },
        ];
    }, [item, resolverAlternatives]);

    return (
        <>
            {noneOfAboveMode ? (
                <NoneOfTheAboveContent onNextHandler={onNextHandler} item={item} />
            ) : (
                <>
                    <div style={{ marginInlineEnd: 8 }}>
                        <BodyText>Looking for one of these properties?</BodyText>
                    </div>
                    {isQueryLoading ? (
                        <ChipListSkeleton
                            testId="not-my-property-skeleton"
                            optionsCount={alternative_count}
                        />
                    ) : (
                        <ChipList
                            testId="not-my-property"
                            options={alternativesOptions}
                            onItemClick={handleOptionClick}
                        />
                    )}
                </>
            )}
        </>
    );
};
